import { html } from '@polymer/polymer/polymer-element.js';
import { DateTime } from 'luxon';
import '@polymer/paper-input/paper-textarea';
import '@polymer/iron-icons/av-icons';
import '@polymer/iron-icons/communication-icons.js';
import '@polymer/iron-form/iron-form.js';
import '@polymer/neon-animation/neon-animation.js';
import '@polymer/paper-menu-button/paper-menu-button.js';
import '@polymer/paper-checkbox/paper-checkbox.js';
import '@polymer/paper-dropdown-menu/paper-dropdown-menu.js';
import '@polymer/iron-dropdown/iron-dropdown.js';
import '../../katapult-auth/katapult-auth';
import { KatapultWidget } from './katapult-widget';
import '../../paper-table/paper-table';
import '../../style-modules/paper-table-style';
import '../../style-modules/paper-dialog-style';
import '../../style-modules/paper-menu-button-style.js';
import '../../katapult-elements/katapult-button.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import { getTeamsUserIsALeaderOf, getCompanyTeamsOnce } from '../../../modules/katapult-company/CompanyTeams.js';
import { ListenToUserRole } from '../../../modules/Roles.js';

class TeamHealthWidget extends KatapultWidget {
  // Widget name
  static get is() {
    return 'katapult-teamhealth-widget';
  }

  // Override properties
  static get properties() {
    return {
      paperHeader: {
        type: Boolean,
        value: false
      },
      primaryControl: {
        type: Boolean,
        value: true
      },
      icon: {
        type: String,
        value: 'hardware:keyboard-arrow-down'
      },
      dataRows: {
        type: Array,
        value: []
      },
      selectedDate: {
        type: String,
        value: null
      },
      selectedMonday: {
        type: String,
        value: null
      },
      workWeekOffset: {
        type: Number,
        value: 0
      },
      displayWeekStart: {
        type: Object,
        value: null
      },
      thMed: {
        type: String,
        value: '0'
      },
      thAvg: {
        type: String,
        value: '0.00'
      },
      thLow: {
        type: String,
        value: '0'
      },
      satTotal: {
        type: Array,
        value: ['0', '0', '0', '0', '0']
      },
      grandTotal: {
        type: String,
        value: '0'
      },
      // team name selected in main dropdown
      selectedTeam: {
        type: String
      },
      srtDscnd: {
        type: Boolean,
        value: false
      },
      numValues: {
        type: Boolean,
        value: false
      },
      // object of all Katapult accounts with each person's UID as the
      // key, "team_health" and "team_id" are the important keys.
      allMembers: {
        type: Object,
        value: {}
      },
      allTeamsUserCanView: {
        type: Object,
        value: {}
      },
      // proper names to fill the main dropdown (also possibly "All Teams")
      teamNames: {
        type: Array,
        value: []
      },
      // UI locking booleans
      dataLoading: {
        type: Boolean,
        value: false
      },
      lockDropdown: {
        type: Boolean,
        value: false
      },
      permLock: {
        type: Boolean,
        value: false
      },
      userIsTimesheetManager: {
        type: Boolean
      },
      reportSortType: {
        type: String,
        value: 'ascending'
      }
    };
  }

  // Override widgetTitleTemplate
  static get widgetTitleTemplate() {
    return html`<span>Team Health</span>`;
  }

  // Override contentTemplate
  static get contentTemplate() {
    return html`
      <p><h1>Content of Widget</h1></p>
    `;
  }

  // Override primaryControlTemplate
  static get primaryControlTemplate() {
    return html`
      <style include="paper-table-style paper-dialog-style paper-menu-button-style">
        :host {
          display: block;
          width: 100%;
        }
        .configurationStepsContainer {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-direction: row;
          margin-left: var(--sl-spacing-2x-small);
          padding: 0px;
        }
        .arrowButton {
          cursor: pointer;
        }
        #dateText {
          text-transform: uppercase;
          width: 200px;
          text-align: center;
        }
      </style>
      <div class="configurationStepsContainer">
        <katapult-button
          color="var(--secondary-color)"
          size="18"
          style="margin-right: 6px;"
          on-click="setToCurrentWeek"
          hidden$="[[shouldHideJumpButton()]]"
          >Today</katapult-button
        >
        <material-icon
          icon="chevron_left"
          class="arrowButton"
          data-prop="displayWeekStart"
          data-offset="-7"
          on-click="updateWeekPointer"
        ></material-icon>
        <span id="dateText">[[getDateRangeString(displayWeekStart, 'DATE_MED', 6)]]</span>
        <material-icon
          icon="chevron_right"
          class="arrowButton"
          data-prop="displayWeekStart"
          data-offset="7"
          on-click="updateWeekPointer"
        ></material-icon>
      </div>
    `;
  }

  // Override bodyTemplate
  static get bodyTemplate() {
    return html`
      <style include="paper-table-style paper-dialog-style paper-menu-button-style">
        :host {
          display: block;
          width: 100%;
        }
        .widgetBody {
          /* override for Widget body paper-table fix in works  */
          justify-content: center;
          padding: 0px;
        }
        .blur {
          font-size: 15px;
          color: transparent;
          text-shadow: 0 0 8px #000;
        }
        .faces {
          height: 30px;
          width: auto;
        }
        .verySad {
          color: #b71c1c;
          margin: auto;
        }
        .sad {
          color: #ff6f00;
          margin: auto;
        }
        .neutral {
          color: #ffab00;
          margin: auto;
        }
        .happy {
          color: #8bc34a;
          margin: auto;
        }
        .veryHappy {
          color: #2e7d32;
          margin: auto;
        }
        .emotionless {
          color: #f1f1f1;
          margin: auto;
        }
        .configurationStepsContainer {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-direction: row;
          margin-left: var(--sl-spacing-2x-small);
          padding: 0px;
        }
        paper-row {
          height: fit-content;
        }
        .column {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
        .employeeTable {
          overflow: hidden;
          margin: var(--sl-spacing-x-large);
          border-radius: var(--sl-button-font-size-large);
        }
        #sortingContainer {
          justify-content: space-evenly;
          align-items: center;
          display: flex;
          flex-direction: row;
        }
        #tuningContainer {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin: 0px var(--sl-spacing-x-large);
        }
      </style>

      <!-- Team Selection and Loading -->
      <paper-table>
        <paper-row>
          <!-- Team Selection and Loading -->
          <paper-cell class="configurationStepsContainer">
            <katapult-icon icon="groups" style="color: var(--primary-color); margin: var(--sl-spacing-small);"></katapult-icon>
            <katapult-drop-down
              disabled="[[lockDropdown]]"
              label="Choose a team"
              items="[[teamNames]]"
              value="{{selectedTeam}}"
              style="max-width: 220px; max-height: 50px"
              no-clear
            ></katapult-drop-down>
            <katapult-button
              id="loadButton"
              disabled="[[dataLoading]]"
              color="var(--secondary-color)"
              on-click="loadReport"
              style="margin: 10px"
              icon="cloud_download"
              >Load</katapult-button
            >
            <template is="dom-if" if="[[dataLoading]]">
              <sl-spinner style="font-size: 25px; margin-left: var(--sl-spacing-x-small);"></sl-spinner>
            </template>
            <span id="reloadMessage" style="margin-left: 10px; color: var(--secondary-text-color);"></span>
          </paper-cell>
        </paper-row>
      </paper-table>

      <!-- No Results Found -->
      <template is="dom-if" if="[[!dataRows.length]]">
        <paper-table style="overflow: hidden;" white outline dividers flex-column white>
          <paper-row white>
            <paper-cell center style="color: var(--primary-text-color-faded);">[[noResultsMessage]]</paper-cell>
          </paper-row>
        </paper-table>
      </template>

      <!-- Report Summary Information -->
      <template is="dom-if" if="[[dataRows.length]]">
        <paper-table style="overflow: hidden;" white outline dividers flex-column white>
          <paper-row style="border-bottom: none">
            <span style="font-size: 14pt; color: var(--primary-text-color-faded); padding: 12px;">Summary</span>
          </paper-row>
          <paper-row>
            <paper-cell class="column">
              <iron-icon icon="social:sentiment-very-dissatisfied" class="faces verySad" style="height: 40px"></iron-icon>
              <span class="verySad"><strong>{{satTotal.0}}</strong></span>
            </paper-cell>
            <paper-cell class="column">
              <iron-icon icon="social:sentiment-dissatisfied" class="faces sad" style="height: 40px"></iron-icon>
              <span class="sad"><strong>{{satTotal.1}}</strong></span>
            </paper-cell>
            <paper-cell class="column">
              <iron-icon icon="social:sentiment-neutral" class="faces neutral" style="height: 40px"></iron-icon>
              <span class="neutral"><strong>{{satTotal.2}}</strong></span>
            </paper-cell>
            <paper-cell class="column">
              <iron-icon icon="social:sentiment-satisfied" class="faces happy" style="height: 40px"></iron-icon>
              <span class="happy"><strong>{{satTotal.3}}</strong></span>
            </paper-cell>
            <paper-cell class="column">
              <iron-icon icon="social:sentiment-very-satisfied" class="faces veryHappy" style="height: 40px"></iron-icon>
              <span class="veryHappy"><strong>{{satTotal.4}}</strong></span>
            </paper-cell>
            <paper-cell class="column">
              <span style="font-size: 20px"><strong>Total</strong></span>
              <span><strong>{{grandTotal}}</strong></span>
            </paper-cell>
          </paper-row>
          <paper-row style="border-bottom: none">
            <paper-cell style="justify-content: center">
              <span><strong>Average:</strong> {{thAvg}} / 5</span>
            </paper-cell>
            <paper-cell style="justify-content: center">
              <span><strong>Median:</strong> {{thMed}} / 5</span>
            </paper-cell>
            <paper-cell style="justify-content: center">
              <span><strong>Unsatisfied:</strong> {{thLow}}</span>
            </paper-cell>
          </paper-row>
        </paper-table>

        <!-- Employee Breakdown -->
        <paper-row style="border-bottom: none">
          <span style="font-size: 14pt; color: var(--primary-text-color-faded); padding: 12px;">Breakdown</span>
        </paper-row>
        <!-- Filters -->
        <paper-row id="tuningContainer">
          <div id="sortingContainer">
            <span style="margin-right: var(--sl-spacing-medium);">Satisfaction Sort Order:</span>
            <paper-radio-group selected="[[reportSortType]]" on-selected-changed="sortTypeChanged">
              <paper-radio-button name="ascending" disabled="[[dataLoading]]">Ascending</paper-radio-button>
              <paper-radio-button name="descending" disabled="[[dataLoading]]">Descending</paper-radio-button>
            </paper-radio-group>
          </div>
          <paper-toggle-button id="numberToggle" on-checked-changed="numToggle" style="margin-right: var(--sl-spacing-medium);"
            >Show Numbers Instead of Faces</paper-toggle-button
          >
        </paper-row>
        <!-- Employee Table -->
        <paper-table class="employeeTable" white outline dividers flex-column white>
          <paper-row raised-on-scroll grey>
            <paper-cell center style="min-width: 120px">Team Member</paper-cell>
            <paper-cell center><strong>This Week</strong></paper-cell>
            <paper-cell center>Last Week</paper-cell>
            <paper-cell center>Two Weeks Ago</paper-cell>
            <paper-cell center>Three Weeks Ago</paper-cell>
            <paper-cell center>Four Weeks Ago</paper-cell>
            <paper-cell style="min-width: 325px">Notes</paper-cell>
          </paper-row>

          <template is="dom-repeat" id="healthTemp" items="[[dataRows]]" as="row" mutable-data>
            <paper-row white>
              <paper-cell center style="min-width: 120px;">[[row.name]]</paper-cell>

              <!-- Satisfaction Value as Numbers -->
              <template is="dom-if" if="[[numValues]]">
                <paper-cell center style="font-size: 20px;" class$="[[getSatColor(row.satisfaction)]]"
                  ><b>[[row.satisfaction]]</b></paper-cell
                >
                <paper-cell center style="font-size: 20px;" class$="[[getSatColor(row.back1Satisfaction)]]"
                  >[[row.back1Satisfaction]]</paper-cell
                >
                <paper-cell center style="font-size: 20px;" class$="[[getSatColor(row.back2Satisfaction)]]"
                  >[[row.back2Satisfaction]]</paper-cell
                >
                <paper-cell center style="font-size: 20px;" class$="[[getSatColor(row.back3Satisfaction)]]"
                  >[[row.back3Satisfaction]]</paper-cell
                >
                <paper-cell center style="font-size: 20px;" class$="[[getSatColor(row.back4Satisfaction)]]"
                  >[[row.back4Satisfaction]]</paper-cell
                >
              </template>

              <!-- Satisfaction Value as Faces -->
              <template is="dom-if" if="[[!numValues]]">
                <paper-cell center>
                  <iron-icon icon="[[getSatFace(row.satisfaction)]]" class$="faces [[getSatColor(row.satisfaction)]]"></iron-icon>
                </paper-cell>
                <paper-cell center>
                  <iron-icon icon="[[getSatFace(row.back1Satisfaction)]]" class$="faces [[getSatColor(row.back1Satisfaction)]]"></iron-icon>
                </paper-cell>
                <paper-cell center>
                  <iron-icon icon="[[getSatFace(row.back2Satisfaction)]]" class$="faces [[getSatColor(row.back2Satisfaction)]]"></iron-icon>
                </paper-cell>
                <paper-cell center>
                  <iron-icon icon="[[getSatFace(row.back3Satisfaction)]]" class$="faces [[getSatColor(row.back3Satisfaction)]]"></iron-icon>
                </paper-cell>
                <paper-cell center>
                  <iron-icon icon="[[getSatFace(row.back4Satisfaction)]]" class$="faces [[getSatColor(row.back4Satisfaction)]]"></iron-icon>
                </paper-cell>
              </template>
              <paper-cell style="min-width: 325px">[[row.gen]]</paper-cell>
            </paper-row>
          </template>
        </paper-table>
      </template>
    `;
  }

  static get observers() {
    return ['defaultSelectedTeam(selectedTeam, teamNames, allTeamsUserCanView)', 'setupRolesListeners(userGroup, user.uid)'];
  }

  // Override headerTemplate
  static get headerTemplate() {
    return html`<paper-cell>Team Health</paper-cell>`;
  }

  ready() {
    super.ready();
    // Initialize date properties to now
    this.selectedDate = DateTime.now();
    this.currentMonday = this.getMonday(this.selectedDate);
    this.selectedMonday = this.currentMonday;
    this.displayWeekStart = this.currentMonday.plus({ days: this.workWeekOffset });
    this.noResultsMessage = 'Select a team to load Team Health results';
  }

  async setupRolesListeners() {
    if (!this.userGroup || !this.user.uid) return;

    // build the default teams list first
    await this.checkTeams();

    // Listen to the timesheet manager role
    this.disconnectTimesheetManagerListener?.();
    this.disconnectTimesheetManagerListener = ListenToUserRole(this.user.uid, 'timesheet_manager', this.userGroup, (isTimesheetManager) => {
      this.userIsTimesheetManager = isTimesheetManager;
      this.checkTeams();
    });
  }

  defaultSelectedTeam() {
    if (Object.keys(this.allTeamsUserCanView).length === 0) return;
    this.selectedTeam ??= this.teamNames?.[0];
  }

  //********************************************************************************************************/
  // Function to load report satisfaction_rating = Number, general_note = Notes
  //********************************************************************************************************/
  loadReport() {
    // let userGroup = document.querySelector('katapult-home').userGroup;
    let weekStart = this.displayWeekStart;
    let weekEndString = this.getWeekEndString(weekStart, 'yyyy-MM-dd', 6);
    // let userGroup = document.querySelector('katapult-home').userGroup;
    let dataRows = [];
    for (let i = 0; i < this.satTotal.length; i++) {
      this.satTotal[i] = '0';
    }
    let total = 0;

    // Change booleans to lock up buttons and show "Loading..." text while loading
    if (!this.permLock) {
      this.lockDropdown = true;
    }
    this.dataLoading = true;

    this.getWeekData(weekEndString, 0, dataRows, total);

    // Set a status message to show
    if (this.dataRows.length == 0) this.noResultsMessage = 'No results found for this team and week';
  }

  getWeekData(weekString, weeksBack, dataRows, total) {
    // Get the data
    return firebase
      .firestore()
      .collection(`companies/${this.userGroup}/time_tracking`)
      .where('week_ending', '==', weekString.toLocaleString())
      .get()
      .then(async (teamH) => {
        // For each teamH
        for (let dat of teamH.docs) {
          let tData = dat.data();
          // Determine if the current team health document should be included in the report
          const teamIdsForUser = this.allMembers[tData.uid]?.team_ids;
          if (teamIdsForUser == null) continue;
          // If the selected team is not "All Teams", only include team health documents with a user that is on the selected team
          if (this.selectedTeam != 'All Teams') {
            const teamHealthDocUserIsOnSelectedTeam = teamIdsForUser.some((teamId) => {
              const teamName = this.allTeamsUserCanView[teamId]?.name;
              if (!teamName) return false;
              return teamName == this.selectedTeam;
            });
            if (!teamHealthDocUserIsOnSelectedTeam) continue;
          }

          // Get the name and satisfaction values
          const email = this.allMembers[tData.uid].email ?? 'unknown@';
          const name = email.substring(0, email.indexOf('@'));
          let satisfaction = tData.growth_rating ?? 0.0;
          // Cut off any decimals from the satisfaction values
          if (!isNaN(satisfaction) && satisfaction >= 1) {
            satisfaction = Math.floor(satisfaction);
          }
          if (weeksBack == 0) {
            this.getDataDistribution(satisfaction);
            if (!isNaN(satisfaction) && satisfaction >= 1) {
              total++;
            }
          }

          let satIndex = dataRows.findIndex((user) => user.name === name);
          if (satIndex > -1) {
            dataRows[satIndex]['back' + weeksBack + 'Satisfaction'] = satisfaction;
          } else if (!isNaN(satisfaction) && satisfaction >= 1 && weeksBack < 1) {
            // Push data to Rows
            dataRows.push({
              recID: dat.id, // id from record
              uid: tData.uid,
              name: name,
              gen: tData.general_note,
              satisfaction: satisfaction
            });
          }
        }

        if (weeksBack < 1) {
          let weekStart = this.displayWeekStart;
          let back1WeekString = this.getWeekEndString(weekStart.plus({ days: -7 }), 'yyyy-MM-dd', 6);
          let back2WeekString = this.getWeekEndString(weekStart.plus({ days: -14 }), 'yyyy-MM-dd', 6);
          let back3WeekString = this.getWeekEndString(weekStart.plus({ days: -21 }), 'yyyy-MM-dd', 6);
          let back4WeekString = this.getWeekEndString(weekStart.plus({ days: -28 }), 'yyyy-MM-dd', 6);
          let promisePast1 = this.getWeekData(back1WeekString, 1, dataRows, total);
          let promisePast2 = this.getWeekData(back2WeekString, 2, dataRows, total);
          let promisePast3 = this.getWeekData(back3WeekString, 3, dataRows, total);
          let promisePast4 = this.getWeekData(back4WeekString, 4, dataRows, total);

          Promise.allSettled([promisePast1, promisePast2, promisePast3, promisePast4]).then(() => {
            // Change booleans to end loading
            if (!this.permLock) {
              this.lockDropdown = false;
            }
            this.dataLoading = false;
            // Copy to page data
            const sortByDescendingSatisfaction = this.sortByDescendingSatisfaction;
            this.dataRows = dataRows.sort(function (a, b) {
              if (sortByDescendingSatisfaction) {
                return b.satisfaction - a.satisfaction;
              } else {
                return a.satisfaction - b.satisfaction;
              }
            });
            this.set('satTotal', [...this.satTotal]);
            this.grandTotal = total.toString();
            // Calculate Average, Median, and Low
            this.getAvg(this.dataRows);
            this.getMed(this.dataRows);
            this.getLow(this.dataRows);
          });
        }
      });
  }

  // Reload report in reverse order
  sortTypeChanged(e) {
    if (!e.detail.value) return;
    this.sortByDescendingSatisfaction = e.detail.value == 'descending';
    this.loadReport();
  }

  // Reload report with numbers or faces
  numToggle(e) {
    if (e.detail.value) {
      this.numValues = true;
    } else {
      this.numValues = false;
    }
  }

  //********************************************************************************************************/
  // Time Picker Stuff
  //********************************************************************************************************/

  shouldHideJumpButton() {
    return this.selectedMonday != this.getWeekEndString(this.currentMonday, 'yyyy-MM-dd', 6);
  }

  getDateRangeString(weekStartDate, format = 'DATE_MED', length = 6) {
    if (!weekStartDate) return;
    // Get the end date by adding length (6 by default) to the start date
    let weekEndDate = weekStartDate.plus({ days: length });
    // Get the formatting object
    return `${weekStartDate.toLocaleString(DateTime[format])} - ${weekEndDate.toLocaleString(DateTime[format])}`;
  }

  getWeekEndString(weekStartDate, format = 'yyyy-MM-dd', length = 6) {
    if (!weekStartDate) return;
    // Get the end date by adding length (6 by default) to the start date
    let weekEndDate = weekStartDate.plus({ days: length });
    // Get the formatting object
    let endWeek = weekEndDate.toFormat(format);
    return `${endWeek.toLocaleString()}`;
  }

  getMonday(date) {
    // Get a new date object from the passed date, and set the weekday to Monday (1)
    let monday = date.set({ weekday: 1 });
    // If the monday is beyond the current date, the week start should be a week sooner
    if (monday.weekday > date.weekday) monday = monday.plus({ days: -7 });
    // Return the date object
    return monday;
  }

  updateWeekPointer(e) {
    // Get data from element dataset
    let prop = e.currentTarget.dataset.prop;
    let offset = parseInt(e.currentTarget.dataset.offset);

    // Update the pointer object
    this[prop] = this[prop]?.plus({ days: offset });
    this.selectedMonday = this.getWeekEndString(this.getMonday(this[prop]), 'yyyy-MM-dd', 6);
    // TODO (01-10-2022): This is hacky: probably ought to just make a "currentTimesheet" var for the review dialog to use...?
    if (prop == 'currentMonday') this.selectedDate = this.currentMonday;
  }

  setToCurrentWeek() {
    // Initialize date properties to now
    this.selectedDate = DateTime.now();
    this.currentMonday = this.getMonday(this.selectedDate);
    this.displayWeekStart = this.currentMonday.plus({ days: this.workWeekOffset });
  }

  jumpToWeek(e) {
    // Get week type from element dataset
    let weekType = e.currentTarget.dataset.weekType;
    // Get some helpful reference dates
    let today = DateTime.fromISO(this.liveIsoDate);
    let monday = this.getMonday(today);

    // If this is a pay week, simply set to monday
    if (weekType == 'pay') this.currentMonday = monday;
    // If this is a work week, set to monday summed with offset (and bump to next week when Sat, Sun, or Fri PM)
    else if (weekType == 'work') {
      let totalOffset = this.workWeekOffset + (today.weekday > 5 || this.liveTimeTag == 'pm' ? 7 : 0);
      this.displayWeekStart = monday.plus({ days: totalOffset });
    }
  }

  //********************************************************************************************************/
  // Helper Functions
  //********************************************************************************************************/

  // Checks if the user has admin or LMA access and gets an array of teams to fill the drop-down menu
  async checkTeams() {
    if (!this.userGroup || !this.user.uid) return;

    // Fetches an object of teams that will be used to fill the dropdown and build out team members
    const allTeamsUserCanView = this.userIsTimesheetManager
      ? await getCompanyTeamsOnce(firebase.firestore(), this.userGroup)
      : await getTeamsUserIsALeaderOf(firebase.firestore(), this.userGroup, this.user.uid);

    // Add teams and email to each member
    await this.buildOutAllMembers(allTeamsUserCanView);

    // Configure the team dropdown to have the proper items and lock state
    this.teamNames = allTeamsUserCanView.map(([, data]) => data.name).sort();
    if (this.teamNames.length > 1) {
      this.teamNames.unshift('All Teams');
      this.lockDropdown = false;
      this.permLock = false;
    } else {
      this.lockDropdown = true;
      this.permLock = true;
    }

    // Store the teams as an object
    this.allTeamsUserCanView = Object.fromEntries(allTeamsUserCanView ?? {});
  }

  async buildOutAllMembers(allTeamsUserCanView) {
    // For each team, get the members and assign the team_id to each member
    for (const [teamId, teamData] of allTeamsUserCanView) {
      const teamMembers = teamData.members;
      for (const uid of teamMembers) {
        this.allMembers[uid] ??= {};
        this.allMembers[uid].team_ids ??= [];
        this.allMembers[uid].team_ids.push(teamId);
      }
    }

    // Get each users email
    const allUsers = await FirebaseWorker.ref(`photoheight/company_space/${this.userGroup}/users`)
      .once('value')
      .then((s) => s.val());
    for (const key in allUsers) {
      const userData = allUsers[key];
      const userUid = userData.uid;
      if (!this.allMembers[userUid]) continue;

      this.allMembers[userUid].email = userData.email;
    }
  }

  // Gets the value for the satisfaction faces
  getSatFace(sat) {
    let face = '';
    if (sat == 1) {
      face = 'social:sentiment-very-dissatisfied';
    } else if (sat == 2) {
      face = 'social:sentiment-dissatisfied';
    } else if (sat == 3) {
      face = 'social:sentiment-neutral';
    } else if (sat == 4) {
      face = 'social:sentiment-satisfied';
    } else if (sat == 5) {
      face = 'social:sentiment-very-satisfied';
    } else {
      face = 'social:sentiment-satisfied';
    }
    return face;
  }

  // Gets the value for the satisfaction face colors
  getSatColor(sat) {
    let color = '';
    if (sat == 1) {
      color = 'verySad';
    } else if (sat == 2) {
      color = 'sad';
    } else if (sat == 3) {
      color = 'neutral';
    } else if (sat == 4) {
      color = 'happy';
    } else if (sat == 5) {
      color = 'veryHappy';
    } else {
      color = 'emotionless';
    }
    return color;
  }

  // Average Calc
  getAvg(n) {
    let total = 0;
    let numLength = 0;
    for (let i = 0; i < n.length; i++) {
      if (!isNaN(n[i].satisfaction) && n[i].satisfaction !== '') {
        total += n[i].satisfaction;
        numLength++;
      }
    }
    if (isNaN(total / numLength)) {
      this.thAvg = '0.00';
    } else {
      this.thAvg = (total / numLength).toFixed(2);
    }
  }

  // Median Calc
  getMed(n) {
    let data = [];

    for (let i = 0; i < n.length; i++) {
      if (!isNaN(n[i].satisfaction) && n[i].satisfaction !== '') {
        data.push(n[i].satisfaction);
      }
    }

    const sorted = data.slice().sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
      this.thMed = ((sorted[middle - 1] + sorted[middle]) / 2).toFixed(2);
    }
    if (isNaN(sorted[middle])) {
      this.thMed = '0';
    } else {
      this.thMed = sorted[middle];
    }
  }

  // Below 3 Count
  getLow(n) {
    let total = 0;
    for (let i = 0; i < n.length; i++) {
      if (!isNaN(n[i].satisfaction) && n[i].satisfaction !== '' && n[i].satisfaction < 3) {
        total++;
      }
    }
    this.thLow = total;
  }

  // Total point ratings for the week
  getDataDistribution(n) {
    let temp;

    if (n > 0 && n < 6) {
      temp = Number(this.satTotal[n - 1]);
      temp++;
      this.satTotal[n - 1] = temp.toString();
    }
  }
} // end module

window.customElements.define('katapult-teamhealth-widget', TeamHealthWidget);
